import { LeanBufferGeometry as BufferGeometry } from "./BufferGeometry";
import { BufferAttribute } from "../../imports/avs";

/**
 * Factory helpers for creating standard geometry primitives as BufferGeometry.
 * Each method returns a mutable BufferGeometry with positions, normals, UVs, indices and optional edge indices pre-filled.
 */
export const GeometryFactory = {

  /**
   * Creates a sphere geometry.
   * @param {number} radius
   * @param {number} widthSegments - Number of horizontal segments (min 3).
   * @param {number} heightSegments - Number of vertical segments (min 2).
   * @returns {BufferGeometry}
   */
  createSphere(radius, widthSegments, heightSegments) {
    widthSegments = Math.max(3, widthSegments || 8);
    heightSegments = Math.max(2, heightSegments || 6);

    const vCount = (widthSegments + 1) * (heightSegments + 1);
    const IndexArray = vCount > 65535 ? Uint32Array : Uint16Array;

    const positions = new Float32Array(vCount * 3);
    const normals = new Float32Array(vCount * 3);
    const uvs = new Float32Array(vCount * 2);
    const indices = new IndexArray(6 * widthSegments * (heightSegments - 1));

    let vi = 0,ui = 0,ii = 0;

    for (let y = 0; y <= heightSegments; y++) {
      const v = y / heightSegments;
      const phi = v * Math.PI;
      for (let x = 0; x <= widthSegments; x++) {
        const u = x / widthSegments;
        const theta = u * Math.PI * 2;

        const nx = -Math.cos(theta) * Math.sin(phi);
        const ny = Math.cos(phi);
        const nz = Math.sin(theta) * Math.sin(phi);

        positions[vi] = nx * radius;positions[vi + 1] = ny * radius;positions[vi + 2] = nz * radius;
        normals[vi] = nx;normals[vi + 1] = ny;normals[vi + 2] = nz;
        vi += 3;
        uvs[ui++] = u;uvs[ui++] = 1 - v;
      }
    }

    for (let y = 0; y < heightSegments; y++) {
      for (let x = 0; x < widthSegments; x++) {
        const a = y * (widthSegments + 1) + x;
        const b = a + widthSegments + 1;
        if (y !== 0) {indices[ii++] = a;indices[ii++] = b;indices[ii++] = a + 1;}
        if (y !== heightSegments - 1) {indices[ii++] = a + 1;indices[ii++] = b;indices[ii++] = b + 1;}
      }
    }

    return _build(positions, normals, uvs, indices);
  },

  /**
   * Creates a box geometry.
   * @param {number} width
   * @param {number} height
   * @param {number} depth
   * @param {boolean} [generateEdges=false] - If true, also generates an edge index buffer with the 12 box edges.
   * @returns {BufferGeometry}
   */
  createBox(width, height, depth, generateEdges = false) {
    const w = width / 2,h = height / 2,d = depth / 2;

    const positions = new Float32Array([
    // +Z
    -w, -h, d, w, -h, d, w, h, d, -w, h, d,
    // -Z
    w, -h, -d, -w, -h, -d, -w, h, -d, w, h, -d,
    // +X
    w, -h, d, w, -h, -d, w, h, -d, w, h, d,
    // -X
    -w, -h, -d, -w, -h, d, -w, h, d, -w, h, -d,
    // +Y
    -w, h, d, w, h, d, w, h, -d, -w, h, -d,
    // -Y
    -w, -h, -d, w, -h, -d, w, -h, d, -w, -h, d]
    );

    const normals = new Float32Array([
    0, 0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 1,
    0, 0, -1, 0, 0, -1, 0, 0, -1, 0, 0, -1,
    1, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0, 0,
    -1, 0, 0, -1, 0, 0, -1, 0, 0, -1, 0, 0,
    0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0,
    0, -1, 0, 0, -1, 0, 0, -1, 0, 0, -1, 0]
    );

    const uvs = new Float32Array([
    0, 0, 1, 0, 1, 1, 0, 1, 0, 0, 1, 0, 1, 1, 0, 1,
    0, 0, 1, 0, 1, 1, 0, 1, 0, 0, 1, 0, 1, 1, 0, 1,
    0, 0, 1, 0, 1, 1, 0, 1, 0, 0, 1, 0, 1, 1, 0, 1]
    );

    const indices = new Uint16Array([
    0, 1, 2, 0, 2, 3, 4, 5, 6, 4, 6, 7,
    8, 9, 10, 8, 10, 11, 12, 13, 14, 12, 14, 15,
    16, 17, 18, 16, 18, 19, 20, 21, 22, 20, 22, 23]
    );

    // 12 box edges. Reuses the +Z face vertex copies (0..3) and -Z face vertex copies (4..7),
    // which together cover all 8 corners. Other faces have their own copies of these positions.
    //   0=(-w,-h, d) 1=( w,-h, d) 2=( w, h, d) 3=(-w, h, d)  (+Z face)
    //   4=( w,-h,-d) 5=(-w,-h,-d) 6=(-w, h,-d) 7=( w, h,-d)  (-Z face)
    const edgeIndices = generateEdges ? new Uint16Array([
    0, 1, 1, 2, 2, 3, 3, 0, // +Z face
    4, 5, 5, 6, 6, 7, 7, 4, // -Z face
    0, 5, 1, 4, 2, 7, 3, 6 // connecting edges
    ]) : null;

    return _build(positions, normals, uvs, indices, edgeIndices);
  },

  /**
   * Creates a cylinder geometry.
   * @param {number} radiusTop
   * @param {number} radiusBottom
   * @param {number} height
   * @param {number} radialSegments
   * @param {boolean} [generateEdges=false] - If true, also generates an edge index buffer with the top and bottom circle edges.
   * @returns {BufferGeometry}
   */
  createCylinder(radiusTop, radiusBottom, height, radialSegments, generateEdges = false) {
    radialSegments = Math.max(3, radialSegments || 8);
    const halfH = height / 2;

    // lateral: 2*(radialSegments+1), top cap: radialSegments+2, bottom cap: radialSegments+2
    const vCount = 4 * radialSegments + 6;
    const IndexArray = vCount > 65535 ? Uint32Array : Uint16Array;

    const positions = new Float32Array(vCount * 3);
    const normals = new Float32Array(vCount * 3);
    const uvs = new Float32Array(vCount * 2);
    const indices = new IndexArray(radialSegments * 12);

    let vi = 0,ui = 0,ii = 0;

    // Lateral surface
    const slope = (radiusBottom - radiusTop) / height;
    const len = Math.sqrt(1 + slope * slope);
    for (let y = 0; y <= 1; y++) {
      const r = y === 0 ? radiusTop : radiusBottom;
      const py = y === 0 ? halfH : -halfH;
      for (let x = 0; x <= radialSegments; x++) {
        const u = x / radialSegments;
        const theta = u * Math.PI * 2;
        const cos = Math.cos(theta);
        const sin = Math.sin(theta);

        positions[vi] = r * cos;positions[vi + 1] = py;positions[vi + 2] = r * sin;
        normals[vi] = cos / len;normals[vi + 1] = slope / len;normals[vi + 2] = sin / len;
        vi += 3;
        uvs[ui++] = u;uvs[ui++] = 1 - y;
      }
    }

    const stride = radialSegments + 1;
    for (let x = 0; x < radialSegments; x++) {
      const a = x,b = x + stride;
      indices[ii++] = a;indices[ii++] = a + 1;indices[ii++] = b;
      indices[ii++] = a + 1;indices[ii++] = b + 1;indices[ii++] = b;
    }

    // Top cap
    const topCenter = vi / 3;
    positions[vi] = 0;positions[vi + 1] = halfH;positions[vi + 2] = 0;
    normals[vi] = 0;normals[vi + 1] = 1;normals[vi + 2] = 0;
    vi += 3;
    uvs[ui++] = 0.5;uvs[ui++] = 0.5;
    for (let x = 0; x <= radialSegments; x++) {
      const u = x / radialSegments;
      const theta = u * Math.PI * 2;
      const cos = Math.cos(theta),sin = Math.sin(theta);
      positions[vi] = radiusTop * cos;positions[vi + 1] = halfH;positions[vi + 2] = radiusTop * sin;
      normals[vi] = 0;normals[vi + 1] = 1;normals[vi + 2] = 0;
      vi += 3;
      uvs[ui++] = cos * 0.5 + 0.5;uvs[ui++] = sin * 0.5 + 0.5;
    }
    for (let x = 0; x < radialSegments; x++) {
      indices[ii++] = topCenter;indices[ii++] = topCenter + 2 + x;indices[ii++] = topCenter + 1 + x;
    }

    // Bottom cap
    const botCenter = vi / 3;
    positions[vi] = 0;positions[vi + 1] = -halfH;positions[vi + 2] = 0;
    normals[vi] = 0;normals[vi + 1] = -1;normals[vi + 2] = 0;
    vi += 3;
    uvs[ui++] = 0.5;uvs[ui++] = 0.5;
    for (let x = 0; x <= radialSegments; x++) {
      const u = x / radialSegments;
      const theta = u * Math.PI * 2;
      const cos = Math.cos(theta),sin = Math.sin(theta);
      positions[vi] = radiusBottom * cos;positions[vi + 1] = -halfH;positions[vi + 2] = radiusBottom * sin;
      normals[vi] = 0;normals[vi + 1] = -1;normals[vi + 2] = 0;
      vi += 3;
      uvs[ui++] = cos * 0.5 + 0.5;uvs[ui++] = sin * 0.5 + 0.5;
    }
    for (let x = 0; x < radialSegments; x++) {
      indices[ii++] = botCenter;indices[ii++] = botCenter + 1 + x;indices[ii++] = botCenter + 2 + x;
    }

    // Top and bottom circle edges. Reuses lateral-surface vertices:
    // row 0 (top, y=halfH) at indices 0..radialSegments, row 1 (bottom) at stride..stride+radialSegments.
    let edgeIndices = null;
    if (generateEdges) {
      edgeIndices = new IndexArray(radialSegments * 4);
      let ei = 0;
      for (let x = 0; x < radialSegments; x++) {
        edgeIndices[ei++] = x;edgeIndices[ei++] = x + 1; // top circle
        edgeIndices[ei++] = stride + x;edgeIndices[ei++] = stride + x + 1; // bottom circle
      }
    }

    return _build(positions, normals, uvs, indices, edgeIndices);
  },

  /**
   * Creates a plane geometry lying in the XY plane.
   * @param {number} width
   * @param {number} height
   * @param {number} [widthSegments=1]
   * @param {number} [heightSegments=1]
   * @param {boolean} [generateEdges=false] - If true, also generates an edge index buffer with the 4 outer edges.
   * @returns {BufferGeometry}
   */
  createPlane(width, height, widthSegments, heightSegments, generateEdges = false) {
    widthSegments = Math.max(1, widthSegments || 1);
    heightSegments = Math.max(1, heightSegments || 1);

    const hw = width / 2,hh = height / 2;
    const dx = width / widthSegments,dy = height / heightSegments;
    const cols = widthSegments + 1;

    const vCount = cols * (heightSegments + 1);
    const IndexArray = vCount > 65535 ? Uint32Array : Uint16Array;

    const positions = new Float32Array(vCount * 3);
    const normals = new Float32Array(vCount * 3);
    const uvs = new Float32Array(vCount * 2);
    const indices = new IndexArray(widthSegments * heightSegments * 6);

    let vi = 0,ui = 0,ii = 0;

    for (let iy = 0; iy <= heightSegments; iy++) {
      const y = iy * dy - hh;
      for (let ix = 0; ix <= widthSegments; ix++) {
        const x = ix * dx - hw;
        positions[vi] = x;positions[vi + 1] = y;positions[vi + 2] = 0;
        normals[vi] = 0;normals[vi + 1] = 0;normals[vi + 2] = 1;
        vi += 3;
        uvs[ui++] = ix / widthSegments;uvs[ui++] = iy / heightSegments;
      }
    }

    for (let iy = 0; iy < heightSegments; iy++) {
      for (let ix = 0; ix < widthSegments; ix++) {
        const a = iy * cols + ix;
        const b = a + cols;
        indices[ii++] = a;indices[ii++] = a + 1;indices[ii++] = b;
        indices[ii++] = a + 1;indices[ii++] = b + 1;indices[ii++] = b;
      }
    }

    // Outer rectangle: 4 corner-to-corner edges, regardless of subdivision.
    let edgeIndices = null;
    if (generateEdges) {
      const bl = 0,br = widthSegments;
      const tl = heightSegments * cols,tr = tl + widthSegments;
      edgeIndices = new IndexArray([bl, br, br, tr, tr, tl, tl, bl]);
    }

    return _build(positions, normals, uvs, indices, edgeIndices);
  }
};

function _build(positions, normals, uvs, indices, edgeIndices) {
  const geom = new BufferGeometry();
  geom.setAttribute('position', new BufferAttribute(positions, 3));
  geom.setAttribute('normal', new BufferAttribute(normals, 3));
  geom.setAttribute('uv', new BufferAttribute(uvs, 2));
  geom.setIndex(indices);
  if (edgeIndices) {
    geom.setEdgeIndices(edgeIndices);
  }
  return geom;
}